import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, CornerDownLeft, FolderPlus, X } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { Toast } from "@/shared/ui/Toast";
import {
  useCollections, useMembership,
  useAddBloggersToCollection, useRemoveBloggerFromCollection, useCreateCollection,
} from "@/shared/api/wb";

type Props = {
  bloggerId: string;
  bloggerName: string;
  onClose: () => void;
};

export function AddToCollectionModal({ bloggerId, bloggerName, onClose }: Props) {
  const { data: collections } = useCollections();
  const { data: membership } = useMembership();
  const add    = useAddBloggersToCollection();
  const remove = useRemoveBloggerFromCollection();
  const create = useCreateCollection();
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [justAdded, setJustAdded] = useState<{ id: string; name: string } | null>(null);

  const inCollections = new Set((membership?.[bloggerId] ?? []).map((c: { id: string }) => c.id));
  const busy = add.isPending || remove.isPending || create.isPending;

  async function toggle(collId: string, collName: string) {
    if (busy) return;
    if (inCollections.has(collId)) {
      await remove.mutateAsync({ id: collId, blogger_id: bloggerId });
      if (justAdded?.id === collId) setJustAdded(null);
    } else {
      await add.mutateAsync({ id: collId, blogger_ids: [bloggerId] });
      setJustAdded({ id: collId, name: collName });
    }
  }

  function startCreating() {
    setCreating(true);
    setNewName("");
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function confirmCreate() {
    if (busy) return;
    const coll = await create.mutateAsync({ name: newName.trim() || undefined });
    await add.mutateAsync({ id: coll.id, blogger_ids: [bloggerId] });
    setJustAdded({ id: coll.id, name: coll.name });
    setCreating(false);
    setNewName("");
  }

  function cancelCreate() {
    setCreating(false);
    setNewName("");
  }

  return (
    <div
      className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-lg shadow-lg w-[min(360px,92vw)] fade-up"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-ink-12">
          <div>
            <div className="font-display font-bold text-[16px] leading-tight">В подборку</div>
            <div className="text-[12.5px] text-ink-40 mt-0.5 truncate max-w-[260px]">{bloggerName}</div>
          </div>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center rounded-md hover:bg-ink-6 text-ink-40">
            <X size={15} />
          </button>
        </div>

        {/* Collection list */}
        <div className="p-3 grid gap-0.5 max-h-[50vh] overflow-y-auto">
          {(collections ?? []).length === 0 && !creating && (
            <div className="text-[13px] text-ink-40 text-center py-6">
              Нет подборок — создайте первую ниже
            </div>
          )}
          {(collections ?? []).map((c) => {
            const inThis = inCollections.has(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggle(c.id, c.name)}
                disabled={busy}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-[13px] transition-colors w-full",
                  inThis ? "bg-lime/8 hover:bg-lime/15" : "hover:bg-ink-6",
                  busy && "opacity-60 cursor-wait",
                )}
              >
                <div className={cn(
                  "w-5 h-5 rounded-sm border-2 grid place-items-center flex-shrink-0 transition-colors",
                  inThis ? "bg-lime border-lime" : "border-ink-12",
                )}>
                  {inThis && <Check size={11} strokeWidth={3} className="text-[var(--fg-on-accent)]" />}
                </div>
                <div className="flex-1 min-w-0 truncate font-medium">{c.name}</div>
                {c.is_active && (
                  <span className="text-[10px] font-mono text-lime flex-shrink-0">активная</span>
                )}
                <span className="text-[11px] text-ink-40 font-mono flex-shrink-0">{c.members_count}</span>
              </button>
            );
          })}
        </div>

        {/* Footer: create new */}
        <div className="px-3 pb-3 border-t border-ink-12 pt-3">
          {creating ? (
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                placeholder="Название подборки"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmCreate();
                  if (e.key === "Escape") cancelCreate();
                }}
                className="flex-1 h-9 px-3 rounded-md border border-ink-12 bg-white text-[13px] outline-none focus:border-ink placeholder:text-ink-30"
              />
              <button
                onClick={confirmCreate}
                disabled={busy}
                title="Создать (Enter)"
                className="w-9 h-9 grid place-items-center rounded-md bg-lime text-[var(--fg-on-accent)] hover:opacity-90 disabled:opacity-50 flex-shrink-0"
              >
                <CornerDownLeft size={14} />
              </button>
              <button
                onClick={cancelCreate}
                title="Отмена"
                className="w-9 h-9 grid place-items-center rounded-md border border-ink-12 text-ink-40 hover:bg-ink-6 flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={startCreating}
              disabled={busy}
              className="flex items-center gap-2 w-full px-3 py-2.5 rounded-md text-[13px] font-medium text-ink-60 hover:bg-ink-6 hover:text-ink border border-dashed border-ink-12 transition-colors disabled:opacity-60"
            >
              <FolderPlus size={14} /> Новая подборка
            </button>
          )}
        </div>
      </div>

      {justAdded && (
        <Toast
          message={`${bloggerName} добавлен в «${justAdded.name}»`}
          onHide={() => setJustAdded(null)}
          action={{
            label: "Открыть подборку",
            onClick: () => { onClose(); navigate(`/collections/${justAdded.id}`); },
          }}
        />
      )}
    </div>
  );
}
